// ============================================================
//  src/pages/ContactPage.js
// ============================================================
import React, { useState } from 'react';

const WA_NUMBER    = '07047358763';
const WA_INTL      = '2347047358763';
const WA_CHANNEL   = 'https://whatsapp.com/channel/0029VbDFmPv0wajrPNfXcX2T';
const WA_CHAT_URL  = `https://wa.me/${WA_INTL}`;

function copyToClipboard(text, label, setCopied) {
  navigator.clipboard?.writeText(text).then(() => {
    setCopied(label);
    setTimeout(() => setCopied(null), 2000);
  });
}

// ── WhatsApp icon SVG ─────────────────────────────────────────
function WhatsAppIcon({ size = 24, color = '#25D366' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
  );
}

// ── Channel icon ──────────────────────────────────────────────
function ChannelIcon({ size = 22 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
    </svg>
  );
}

// ── Contact card ──────────────────────────────────────────────
function ContactCard({ icon, title, subtitle, value, displayValue, href, copyValue, copyLabel, accent, copied, setCopied, badge }) {
  return (
    <div style={{
      background: 'var(--bg-card)',
      border: '1.5px solid var(--border)',
      borderRadius: 'var(--radius-lg)',
      overflow: 'hidden',
      transition: 'box-shadow 0.2s, border-color 0.2s',
    }}
      onMouseEnter={e => {
        e.currentTarget.style.borderColor = accent;
        e.currentTarget.style.boxShadow = `0 4px 24px ${accent}18`;
      }}
      onMouseLeave={e => {
        e.currentTarget.style.borderColor = 'var(--border)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      {/* Accent top stripe */}
      <div style={{ height: 3, background: `linear-gradient(90deg, ${accent}, ${accent}66)` }} />

      <div style={{ padding: '28px 28px 24px' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{
              width: 52, height: 52, borderRadius: 14,
              background: accent + '15',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              {icon}
            </div>
            <div>
              <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--text-dark)', marginBottom: 2 }}>{title}</div>
              <div style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.4 }}>{subtitle}</div>
            </div>
          </div>
          {badge && (
            <span style={{
              fontSize: 10, fontWeight: 800, letterSpacing: '0.5px',
              textTransform: 'uppercase', padding: '3px 10px',
              borderRadius: 20, background: accent + '15', color: accent,
              flexShrink: 0,
            }}>{badge}</span>
          )}
        </div>

        {/* Value display */}
        <div style={{
          background: 'var(--surface)', border: '1.5px solid var(--border)',
          borderRadius: 'var(--radius-sm)', padding: '12px 14px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 10, marginBottom: 16,
        }}>
          <span style={{
            fontSize: 14, fontWeight: 700, color: 'var(--text-dark)',
            fontFamily: 'monospace', letterSpacing: '0.3px',
            flex: 1, wordBreak: 'break-all',
          }}>
            {displayValue || value}
          </span>
          <button
            onClick={() => copyToClipboard(copyValue || value, copyLabel, setCopied)}
            style={{
              flexShrink: 0, background: copied === copyLabel ? accent : 'var(--bg-card)',
              color: copied === copyLabel ? '#fff' : 'var(--text-muted)',
              border: `1.5px solid ${copied === copyLabel ? accent : 'var(--border)'}`,
              borderRadius: 8, padding: '5px 14px', fontSize: 12, fontWeight: 700,
              cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s',
            }}
          >
            {copied === copyLabel ? '✓ Copied' : 'Copy'}
          </button>
        </div>

        {/* CTA button */}
        <a
          href={href}
          target="_blank"
          rel="noreferrer"
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            gap: 8, width: '100%', padding: '13px 0',
            background: accent, color: '#fff',
            borderRadius: 'var(--radius-sm)', border: 'none',
            fontSize: 14, fontWeight: 800, cursor: 'pointer',
            textDecoration: 'none', transition: 'opacity 0.15s',
            boxSizing: 'border-box',
          }}
          onMouseEnter={e => e.currentTarget.style.opacity = '0.88'}
          onMouseLeave={e => e.currentTarget.style.opacity = '1'}
        >
          <WhatsAppIcon size={18} color="#fff" />
          {title === 'WhatsApp Channel' ? 'Join Channel' : 'Message Us'}
        </a>
      </div>
    </div>
  );
}

// ── FAQ item ──────────────────────────────────────────────────
function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      onClick={() => setOpen(o => !o)}
      style={{
        border: '1.5px solid var(--border)', borderRadius: 'var(--radius-sm)',
        marginBottom: 8, overflow: 'hidden', cursor: 'pointer',
        transition: 'border-color 0.15s',
        borderColor: open ? 'var(--primary)' : 'var(--border)',
      }}
    >
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '14px 16px', background: open ? 'rgba(0,200,140,0.04)' : 'var(--bg-card)',
      }}>
        <span style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--text-dark)', flex: 1, paddingRight: 12 }}>{q}</span>
        <span style={{
          fontSize: 11, color: 'var(--primary)', fontWeight: 800,
          transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s',
        }}>▼</span>
      </div>
      {open && (
        <div style={{
          padding: '0 16px 14px', fontSize: 13.5, color: 'var(--text-mid)',
          lineHeight: 1.65, borderTop: '1px solid var(--border)',
          paddingTop: 12, background: 'var(--surface)',
        }}>{a}</div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────
export default function ContactPage() {
  const [copied, setCopied] = useState(null);

  return (
    <div style={{
      minHeight: '100vh', background: 'var(--page-bg)',
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    }}>
      <div style={{ maxWidth: 680, margin: '0 auto', padding: 'clamp(28px, 6vw, 48px) clamp(16px, 5vw, 24px) 64px' }}>

        {/* ── Hero ── */}
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: 72, height: 72, borderRadius: 20,
            background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
            marginBottom: 18, boxShadow: '0 8px 24px rgba(37,211,102,0.28)',
          }}>
            <WhatsAppIcon size={36} color="#fff" />
          </div>
          <h1 style={{
            margin: '0 0 10px', fontSize: 'clamp(24px, 6vw, 30px)',
            fontWeight: 900, color: 'var(--text-dark)', letterSpacing: '-0.4px',
          }}>
            Contact Us
          </h1>
          <p style={{
            margin: 0, fontSize: 'clamp(13.5px, 4vw, 15px)',
            color: 'var(--text-muted)', lineHeight: 1.6, maxWidth: 420, marginInline: 'auto',
          }}>
            We're available on WhatsApp. Reach out directly or follow our channel for updates, tips, and announcements.
          </p>
        </div>

        {/* ── Response time banner ── */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'center',
          padding: '10px 20px', background: 'rgba(0,200,140,0.07)',
          border: '1.5px solid rgba(0,200,140,0.20)', borderRadius: 40,
          marginBottom: 32, width: 'fit-content', marginInline: 'auto',
        }}>
          <span style={{
            width: 8, height: 8, borderRadius: '50%',
            background: '#25D366', display: 'inline-block',
            boxShadow: '0 0 0 3px rgba(37,211,102,0.25)',
            animation: 'pulse 2s ease infinite',
          }} />
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--primary)' }}>
            Typically replies within a few hours
          </span>
        </div>

        <style>{`
          @keyframes pulse {
            0%, 100% { box-shadow: 0 0 0 3px rgba(37,211,102,0.25); }
            50%       { box-shadow: 0 0 0 6px rgba(37,211,102,0.10); }
          }
        `}</style>

        {/* ── Cards ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 36 }}>
          <ContactCard
            icon={<WhatsAppIcon size={26} color="#25D366" />}
            title="WhatsApp Support"
            subtitle="Direct chat with our support team"
            value={WA_NUMBER}
            displayValue={WA_NUMBER}
            href={WA_CHAT_URL}
            copyValue={WA_NUMBER}
            copyLabel="phone"
            accent="#25D366"
            badge="Support"
            copied={copied}
            setCopied={setCopied}
          />
          <ContactCard
            icon={<ChannelIcon size={24} />}
            title="WhatsApp Channel"
            subtitle="Follow for platform updates, tips & new features"
            value={WA_CHANNEL}
            displayValue="@CmdKit Official Channel"
            href={WA_CHANNEL}
            copyValue={WA_CHANNEL}
            copyLabel="channel"
            accent="#128C7E"
            badge="Updates"
            copied={copied}
            setCopied={setCopied}
          />
        </div>

        {/* ── What to expect ── */}
        <div style={{
          background: 'var(--bg-card)', border: '1.5px solid var(--border)',
          borderRadius: 'var(--radius-lg)', padding: '24px 24px 20px', marginBottom: 24,
        }}>
          <div style={{
            fontSize: 11, fontWeight: 800, color: 'var(--text-muted)',
            textTransform: 'uppercase', letterSpacing: '0.7px', marginBottom: 18,
          }}>
            What we can help with
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10 }}>
            {[
              { icon: '🤖', label: 'Bot setup & configuration' },
              { icon: '🌊', label: 'WhatsApp Flows help' },
              { icon: '🔗', label: 'Webhook issues' },
              { icon: '🛠️', label: 'Technical debugging' },
              { icon: '💳', label: 'Billing & plan questions' },
              { icon: '🚀', label: 'Feature requests' },
            ].map(({ icon, label }) => (
              <div key={label} style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 12px', background: 'var(--surface)',
                border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
              }}>
                <span style={{ fontSize: 17, flexShrink: 0 }}>{icon}</span>
                <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-mid)', lineHeight: 1.3 }}>{label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* ── FAQ ── */}
        <div style={{
          background: 'var(--bg-card)', border: '1.5px solid var(--border)',
          borderRadius: 'var(--radius-lg)', padding: '24px 24px 20px',
        }}>
          <div style={{
            fontSize: 11, fontWeight: 800, color: 'var(--text-muted)',
            textTransform: 'uppercase', letterSpacing: '0.7px', marginBottom: 16,
          }}>
            Frequently asked
          </div>
          {[
            {
              q: "What are your support hours?",
              a: "We monitor WhatsApp daily and typically respond within a few hours. For urgent issues, message us directly and mention it is urgent.",
            },
            {
              q: "I sent a message but have not heard back yet",
              a: "If it has been more than 24 hours, please send a follow-up message. Sometimes messages get missed. We are a small team and do our best to respond promptly.",
            },
            {
              q: "What should I include when asking for help?",
              a: "Your bot ID, a description of what you are trying to do, what you expected to happen, and what actually happened. Screenshots of any errors from the Logs tab are especially useful.",
            },
            {
              q: "Can I get help with building my bot flow?",
              a: "Yes! We are happy to help you think through your bot logic, review your JSON actions, or troubleshoot a command that is not behaving as expected.",
            },
            {
              q: "Where can I follow platform announcements?",
              a: "Join our WhatsApp Channel above. We post updates about new features, maintenance windows, and tips for getting the most out of the platform.",
            },
          ].map(faq => <FaqItem key={faq.q} q={faq.q} a={faq.a} />)}
        </div>

      </div>
    </div>
  );
}